import {Injectable} from '@angular/core';
import * as storeActions from '@catcore/actions/features';
import {FeatureType, FeatureTypeCreateInfo} from '@catcore/services/feature-type/feature-type';
import {Feature} from '@catcore/services/feature/feature';
import {RestHttpService} from '@catcore/services/rest-http.service';
import {GenericLinkedResource} from '@catcore/types/base';
import {extractDataFromProperty} from '@catcore/utils';
import {GenericResourceService} from '@catfeatures/services/generic-resource.service';
import {Resource} from '@catfeatures/types';
import {Store} from '@ngrx/store';
import {Restangular} from 'ngx-restangular';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';

@Injectable()
export class FeatureTypeService extends RestHttpService<Feature> {

  constructor(service: Restangular,
              private genericResourceService: GenericResourceService,
              private store: Store<any>) {
    super('features', service);
  }

  getServices(id: string): Subject<FeatureType[]> {
    return this.getListForResource(id, 'services');
  }

  getPorts(feature: Feature) {
    const resourceId = `/api/features/${feature.id}/ports`;
    return this.genericResourceService.findServiceForResource(resourceId);
  }

  extractPortsFor(feature: Feature): GenericLinkedResource[] {
    const lookupKey = 'action:ports';
    return extractDataFromProperty(feature, lookupKey);
  }

  extractGgwDevicesFor(feature: Feature): any[] {
    const lookupKey = 'ggw_devices';
    return extractDataFromProperty(feature, lookupKey);
  }

  extractGgwImplementationsFor(feature: Feature): any[] {
    const lookupKey = 'action:ggw-implementations';
    return extractDataFromProperty(feature, lookupKey);
  }

  extractFixedRoutesFor(feature: Feature): any[] {
    const lookupKey = 'action:ggw-fixed-routes';
    return extractDataFromProperty(feature, lookupKey);
  }

  extractRouterConfigFor(feature: Feature): any[] {
    const lookupKey = 'action:ggw-fixed-routes';
    return extractDataFromProperty(feature, lookupKey);
  }

  extractEvidFunctionsFor(feature: Feature): any[] {
    const lookupKey = 'evid_function_types';
    return extractDataFromProperty(feature, lookupKey);
  }

  extractUnitsFor(feature: Feature): GenericLinkedResource[] {
    const lookupKey = 'unit_types';
    return extractDataFromProperty(feature, lookupKey);
  }

  extractConfigurableUnitsFor(feature: Feature): GenericLinkedResource[] {
    const lookupKey = 'unit_types';
    const configurableUnits: GenericLinkedResource[] = [];
    const units = extractDataFromProperty(feature, lookupKey, '_embedded');
    const unitLinks = this.extractUnitsFor(feature);
    for (let i = 0; i < unitLinks.length; i++) {
      if  (units[i].configurable === true) {
        configurableUnits.push(unitLinks[i]);
      }
    }
    return configurableUnits;
  }

  getGgwDevices(id: string) {
    return this.getAllSubresources(id, 'ggw-devices');
  }

  getGgwImplementations(id: string): Subject<FeatureType[]> {
    return this.getListForResource(id, 'ggw-implementations');
  }

  getGgwFixedRoutes(id: string): Subject<FeatureType[]> {
    return this.getListForResource(id, 'ggw-fixed-routes');
  }

  getBlockDiagram(id: string): Subject<FeatureType> {
    return this.getResource(id, 'block-diagram');
  }

  getGgwParameterOptions(id: string, parameterType: string = null) {
    const params = (parameterType ? {parameter_type: parameterType} : {});
    return this
      .getServiceForOne(id)
      .all('ggw-parameter-options')
      .customGET('', params);
  }

  /**
   * Returns all option tokens available within the given feature.
   *
   * @param feature
   */
  getOptionTokens(feature: Feature) {
    return this.getAllSubresources(feature.id, 'option-tokens');
  }

  create(item: FeatureTypeCreateInfo): Observable<any> {
    return this.queryAll()
      .post({
        name: item.name,
        description: item.description,
        status: item.status._links.self.href,
        publicFlag: item.isPublic,
      })
      .map((createdFeature) => {
        this.store.dispatch({
            type: storeActions.CORE_FEATURE_CREATED,
            payload: {feature: createdFeature},
          },
        );
        return createdFeature;
      });
  }

  delete(feature: Feature) {
    return super.delete(feature)
      .map((deletedFeature) => {
        this.store.dispatch({
            type: storeActions.CORE_FEATURE_DELETED,
            payload: {feature: deletedFeature},
          },
        );
        return deletedFeature;
      })
  }

  clone(feature: Feature) {
      return super.clone(feature)
          .map((clonedFeature) => {
              this.store.dispatch({
                      type: storeActions.CORE_FEATURE_CREATED,
                      payload: {feature: clonedFeature},
                  },
              );
              return clonedFeature;
          })
  }

  private getListForResource(id: string, resourceName: string) {
    return this
      .getServiceForOne(id)
      .all(resourceName)
      .getList();
  }

  private getResource(id: string, resourceName: string) {
    return this
      .getServiceForOne(id)
      .all(resourceName)
      .customGET('');
  }

}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/core/services/feature-type/feature-type.service.ts